/* eslint-disable check-file/folder-naming-convention */
import axios from 'axios';
import { v4 } from 'uuid';

const LOCALSTORAGE_COUNTRYCODE_KEY = 'xdesign-utils__country-code';
const LOCALSTORAGE_IP_KEY = 'xdesign-utils__ip';
const LOCALSTORAGE_IS_OFFICE_IP_KEY = 'xdesign-utils__is-office-ip';
const DEFAULT_IP_LOCATION_TIMEOUT = 3000;
// 是否更新过本地国家码缓存
let hasUpdateLocalCountryCode = false;
// 正在请求状态
let fetchLoading = false;
let processingFetch: (value: string) => void = () => {};
let instanceFetch: any = null;
// 按是否识别办公内网隔离结果和进行中的请求，避免内网判断复用普通国家码查询。
const cachedIpLocations = new Map<boolean, IpLocationInfo>();
const sharedIpLocationFetches = new Map<boolean, Promise<IpLocationInfo | null>>();

/**
 * IP 归属接口解析后的首个访问 IP 信息。
 */
export interface IpLocationInfo {
    /** 当前请求解析出的公网 IP */
    ip: string;
    /** 国家/地区 ID */
    country_id?: number;
    /** 国家/地区码，例如 CN、SG */
    nation_code: string;
    /** 是否为办公内网 IP；只有布尔 true 表示命中 */
    is_office_ip?: boolean;
}

/**
 * getIpLocation 的请求选项与缓存读取策略。
 */
export interface GetIpLocationOptions {
    /** 是否让服务端识别办公内网 IP；仅 true 时开启，且不复用无法确认识别状态的本地缓存 */
    identify_office?: boolean;
    /**
     * 是否优先等待本轮网络结果。
     *
     * 首页 IP 强跳会改变页面位置，需要优先使用当前网络环境的结果；超时或失败后再回退本地缓存。
     */
    preferFresh?: boolean;
    /**
     * preferFresh 网络失败时是否回退本地缓存。
     *
     * 会改写 location 的强跳场景需要关闭该项，避免使用过期 localStorage 结果跳转。
     */
    fallbackToLocalCache?: boolean;
}

const getRequestUrl = () => {
    const env = process.env.REACT_APP_ENV || process.env.NEXT_PUBLIC_ENV;
    const isDev = process.env.NODE_ENV === 'development';

    if (window.isIntl && window.deployApp === 'designkit') {
        let host = 'www.designkit.com';

        if (env === 'pre' || isDev) {
            host = 'pre.designkit.com';
        } else if (env === 'beta') {
            host = 'beta.designkit.com';
        }

        return `https://${host}/api`;
    }

    if (window.isIntl) {
        let host = 'zawa.ai';

        if (env === 'pre' || isDev) {
            host = 'pre.zawa.ai';
        } else if (env === 'beta') {
            host = 'beta.zawa.ai';
        }

        return `https://${host}/api`;
    }

    const hostPrefix = env === 'pre' || isDev ? 'pre' : env === 'beta' ? 'beta' : '';

    return `https://${hostPrefix}webapi.designkit.cn`;
};

const parseIpLocationInfo = (data: Record<string, Omit<IpLocationInfo, 'ip'>>): IpLocationInfo | null => {
    const ip = Object.keys(data)[0] || '';
    const info = ip ? data[ip] : undefined;

    if (!ip || !info?.nation_code) return null;

    return {
        ...info,
        ip,
    };
};

/**
 * 读取可供 getIpLocation 直接使用的本地 IP 归属缓存。
 *
 * getCountryCode 历史上只依赖 country_code；本次新增办公 IP 分流后，完整缓存必须同时包含
 * country_code 和 is_office_ip。缺少 is_office_ip 说明是旧缓存，需要重新请求补齐。
 *
 * @returns 本地缓存完整时返回 IP 归属信息，否则返回 null
 */
const getLocalIpLocation = (): IpLocationInfo | null => {
    const countryCode = window?.localStorage?.getItem(LOCALSTORAGE_COUNTRYCODE_KEY);
    const isOfficeIp = window?.localStorage?.getItem(LOCALSTORAGE_IS_OFFICE_IP_KEY);

    if (!countryCode || isOfficeIp === null) return null;

    return {
        ip: window?.localStorage?.getItem(LOCALSTORAGE_IP_KEY) || '',
        nation_code: countryCode,
        is_office_ip: isOfficeIp === 'true',
    };
};

/**
 * 写入 IP 归属本地缓存。
 *
 * 复用原有 country_code/ip key，并新增 is_office_ip key；办公 IP 只有布尔 true 才写为 true。
 *
 * @param ipLocation - 接口解析后的 IP 归属信息
 */
const setLocalIpLocation = (ipLocation: IpLocationInfo) => {
    window.localStorage.setItem(LOCALSTORAGE_COUNTRYCODE_KEY, ipLocation.nation_code);
    window.localStorage.setItem(LOCALSTORAGE_IP_KEY, ipLocation.ip);
    window.localStorage.setItem(LOCALSTORAGE_IS_OFFICE_IP_KEY, String(ipLocation.is_office_ip === true));
};

/**
 * 发起无业务副作用的 IP 归属请求。
 *
 * 该函数只请求和解析数据，不写 localStorage，也不维护 getCountryCode 的 processingFetch/fetchLoading。
 * 业务语义由上层调用方自行处理，避免新增 getIpLocation 影响 getCountryCode 的既有流程。
 *
 * @param identifyOffice - 是否让服务端识别办公内网 IP，默认不开启
 * @returns IP 归属信息；接口异常由共享层兜底为 null
 */
const requestIpLocationRaw = async (identifyOffice = false): Promise<IpLocationInfo | null> => {
    const res = await axios(`${getRequestUrl()}/common/ip_location`, {
        params: { ts_random_id: v4(), ...(identifyOffice ? { identify_office: true } : {}) },
        timeout: DEFAULT_IP_LOCATION_TIMEOUT,
    });
    const data = res?.data?.data || {};

    return parseIpLocationInfo(data);
};

/**
 * 复用同一轮 IP 归属请求。
 *
 * 仅在办公内网识别选项相同时共享请求，避免首页分流拿到未经内网判断的结果；
 * 这里仍只维护完整 IP 信息的内存缓存，不接管 getCountryCode 的本地缓存和状态机。
 *
 * @param identifyOffice - 是否让服务端识别办公内网 IP，默认不开启
 * @returns IP 归属信息；请求失败时返回 null
 */
const getSharedIpLocation = async (identifyOffice = false): Promise<IpLocationInfo | null> => {
    const cachedIpLocation = cachedIpLocations.get(identifyOffice);
    if (cachedIpLocation) return cachedIpLocation;

    let sharedIpLocationFetch = sharedIpLocationFetches.get(identifyOffice);
    if (!sharedIpLocationFetch) {
        sharedIpLocationFetch = requestIpLocationRaw(identifyOffice)
            .then((ipLocation) => {
                if (ipLocation) {
                    cachedIpLocations.set(identifyOffice, ipLocation);
                }

                return ipLocation;
            })
            .catch(() => null)
            // 项目使用 ES6 类型库；异常已转为 null，用 then 统一清理并透传结果，避免依赖 Promise.finally。
            .then((ipLocation) => {
                sharedIpLocationFetches.delete(identifyOffice);
                return ipLocation;
            });
        sharedIpLocationFetches.set(identifyOffice, sharedIpLocationFetch);
    }

    return await sharedIpLocationFetch;
};

/**
 * 按调用方的 timeout 等待共享 IP 请求。
 *
 * 底层请求可以继续完成并供后续调用复用，但当前调用不会超过自己的等待上限；
 * 这样 getCountryCode 的 200ms 首次等待策略不会被 getIpLocation 的 3000ms 策略拖长。
 *
 * @param timeout - 当前调用愿意等待 IP 归属结果的最长时间
 * @param identifyOffice - 是否让服务端识别办公内网 IP，默认不开启
 * @returns 超时或失败时返回 null
 */
const waitIpLocationWithTimeout = async (timeout: number, identifyOffice = false): Promise<IpLocationInfo | null> => {
    return await Promise.race([
        getSharedIpLocation(identifyOffice),
        new Promise<null>((resolve) => {
            window.setTimeout(() => resolve(null), timeout);
        }),
    ]);
};

const fetchRequest = async (timeout: number) => {
    const defaultCode = window.isIntl ? 'SG' : 'CN';
    try {
        fetchLoading = true;
        // 只共享底层网络请求；getCountryCode 的默认值、localStorage 写入和并发回调仍在这里处理。
        const ipLocation = await waitIpLocationWithTimeout(timeout);
        const code = ipLocation?.nation_code;

        if (ipLocation) {
            hasUpdateLocalCountryCode = true;
            // 更新缓存
            setLocalIpLocation(ipLocation);
        }
        processingFetch(code || defaultCode);
        fetchLoading = false;
    } catch (e) {
        processingFetch(defaultCode);
        fetchLoading = false;
    }
};

/**
 * 刷新本地 IP 归属缓存。
 *
 * getIpLocation 命中 localStorage 后仍需要触发一次后台刷新，避免用户切换网络后长期沿用旧国家码或办公 IP 标记。
 * 请求成功时更新本地 IP 归属缓存。
 *
 * @param timeout - 当前调用愿意等待 IP 归属结果的最长时间
 * @param identifyOffice - 是否让服务端识别办公内网 IP，默认不开启
 * @returns 网络刷新成功时返回 IP 归属信息，否则返回 null
 */
const refreshLocalIpLocation = async (timeout: number, identifyOffice = false): Promise<IpLocationInfo | null> => {
    const ipLocation = await waitIpLocationWithTimeout(timeout, identifyOffice);

    if (ipLocation) {
        hasUpdateLocalCountryCode = true;
        setLocalIpLocation(ipLocation);
    }

    return ipLocation;
};

const handleGetCodeProcess = async (timeout?: number) => {
    fetchRequest(timeout || 200);

    return await new Promise((resolve) => (processingFetch = resolve));
};

/**
 * 获取云储存分摊埋点透传值
 * @param timeout ip_location接口超时时间
 * @returns
 */
const getCountryCode = async (timeout?: number) => {
    let country_code;

    // 本地缓存的country_code
    const country_code_local = window?.localStorage?.getItem(LOCALSTORAGE_COUNTRYCODE_KEY);

    if (!country_code_local) {
        // 处理并发情况
        country_code = instanceFetch ? await instanceFetch : await (instanceFetch = handleGetCodeProcess(timeout));

        // 成功后释放实例
        instanceFetch = null;
    } else {
        country_code = country_code_local;
    }

    // 如果还没请求过接口，异步更新本地缓存
    if (!hasUpdateLocalCountryCode && !fetchLoading) {
        fetchRequest(3000);
    }

    return country_code;
};

/**
 * 获取当前用户 IP 归属信息。
 *
 * 与 getCountryCode 使用同一个底层接口，但返回完整 IP 归属结果，便于业务读取 is_office_ip 等扩展字段。
 * 请求成功时更新本地 IP 归属缓存。
 *
 * @param timeout - ip_location 接口超时时间
 * @param options - 办公内网识别选项与缓存读取策略
 * @returns IP 归属信息；接口失败或返回异常时为 null
 */
const getIpLocation = async (timeout?: number, options: GetIpLocationOptions = {}): Promise<IpLocationInfo | null> => {
    const requestTimeout = timeout || DEFAULT_IP_LOCATION_TIMEOUT;
    const identifyOffice = options.identify_office === true;
    // 旧本地缓存未记录是否请求过内网识别，开启识别时只使用对应的网络结果或内存缓存。
    const localIpLocation = identifyOffice ? null : getLocalIpLocation();

    if (options.preferFresh) {
        const freshIpLocation = await refreshLocalIpLocation(requestTimeout, identifyOffice);

        if (freshIpLocation || options.fallbackToLocalCache === false) {
            return freshIpLocation;
        }

        return localIpLocation;
    }

    if (localIpLocation) {
        void refreshLocalIpLocation(DEFAULT_IP_LOCATION_TIMEOUT, identifyOffice);
        return localIpLocation;
    }

    return await refreshLocalIpLocation(requestTimeout, identifyOffice);
};

/**
 * 获取当前用户 IP 地址（同步，仅读取缓存）
 */
const getIpAddress = () => {
    return window?.localStorage?.getItem(LOCALSTORAGE_IP_KEY) || '';
};

/**
 * 获取当前用户 IP 地址（异步，无缓存时会等待请求完成）
 * @param timeout ip_location接口超时时间
 */
const getIpAddressAsync = async (timeout?: number) => {
    const ip_local = window?.localStorage?.getItem(LOCALSTORAGE_IP_KEY);

    if (ip_local) {
        if (!hasUpdateLocalCountryCode && !fetchLoading) {
            fetchRequest(3000);
        }
        return ip_local;
    }

    await getCountryCode(timeout);
    return window?.localStorage?.getItem(LOCALSTORAGE_IP_KEY) || '';
};

export { getIpAddress, getIpAddressAsync, getIpLocation };
export default getCountryCode;
